import { gql } from '@apollo/client'

// ==========================================
// MENU MUTATIONS
// ==========================================

export const CREATE_ADVANCED_MENU = gql`
  mutation CreateAdvancedMenu($restaurantId: ID!, $input: AdvancedMenuInput!) {
    createAdvancedMenu(restaurantId: $restaurantId, input: $input) {
      _id
      restaurantId
      name
      description
      menuType
      startTime
      endTime
      daysOfWeek
      daySchedule
      isActive
      displayOrder
      markupPercent
      roundingMode
      roundingValue
      categoryOverrides
      itemOverrides
      createdAt
      updatedAt
    }
  }
`

export const UPDATE_ADVANCED_MENU = gql`
  mutation UpdateAdvancedMenu($id: ID!, $input: AdvancedMenuInput!) {
    updateAdvancedMenu(id: $id, input: $input) {
      _id
      name
      menuType
      isActive
      markupPercent
      roundingMode
      roundingValue
      categoryOverrides
      itemOverrides
      updatedAt
    }
  }
`

export const DELETE_ADVANCED_MENU = gql`
  mutation DeleteAdvancedMenu($id: ID!) {
    deleteAdvancedMenu(id: $id)
  }
`

// ==========================================
// ITEM MUTATIONS
// ==========================================

export const CREATE_ADVANCED_MENU_ITEM = gql`
  mutation CreateAdvancedMenuItem($restaurantId: ID!, $input: AdvancedMenuItemInput!) {
    createAdvancedMenuItem(restaurantId: $restaurantId, input: $input) {
      _id
      menuId
      restaurantId
      name
      description
      imageUrl
      categoryName
      pricingMode
      basePrice
      variantPrices
      isActive
      is86ed
      createdAt
      updatedAt
    }
  }
`

export const UPDATE_ADVANCED_MENU_ITEM = gql`
  mutation UpdateAdvancedMenuItem($id: ID!, $input: AdvancedMenuItemInput!) {
    updateAdvancedMenuItem(id: $id, input: $input) {
      _id
      name
      pricingMode
      basePrice
      variantPrices
      updatedAt
    }
  }
`

export const DELETE_ADVANCED_MENU_ITEM = gql`
  mutation DeleteAdvancedMenuItem($id: ID!) {
    deleteAdvancedMenuItem(id: $id)
  }
`

export const GENERATE_ITEM_AI_CONTENT = gql`
  mutation GenerateItemAIContent($id: ID!, $image: Boolean, $description: Boolean) {
    generateItemAIContent(id: $id, image: $image, description: $description) {
      _id
      imageUrl
      aiImageUrl
      description
      aiDescription
    }
  }
`

export const CLEAR_ITEM_AI_CONTENT = gql`
  mutation ClearItemAIContent($id: ID!, $image: Boolean, $description: Boolean) {
    clearItemAIContent(id: $id, image: $image, description: $description) {
      _id
      imageUrl
      aiImageUrl
      description
      aiDescription
    }
  }
`

export const GENERATE_ALL_ITEMS_AI_CONTENT = gql`
  mutation GenerateAllItemsAIContent($restaurantId: ID!) {
    generateAllItemsAIContent(restaurantId: $restaurantId) {
      queued
      estimatedMinutes
      started
    }
  }
`

// Background batch that translates item names + category titles into the
// restaurant's selected menu languages. force = redo names that already have a
// translation.
export const TRANSLATE_MENU_NAMES = gql`
  mutation TranslateMenuNames($restaurantId: ID!, $force: Boolean) {
    translateMenuNames(restaurantId: $restaurantId, force: $force) {
      queued
      estimatedMinutes
      started
    }
  }
`

export const GET_MENU_LANGUAGES = gql`
  query GetMenuLanguages($id: String!) {
    restaurant(id: $id) {
      _id
      menuLanguage2
      menuLanguage3
      menuLanguageDisplay
      menuLanguageKitchenDisplay
      menuLanguageReceiptDisplay
      menuLanguageScreenDisplay
    }
  }
`

export const SET_MENU_LANGUAGES = gql`
  mutation SetMenuLanguages($restaurantId: ID!, $language2: String, $language3: String, $displayMode: String!) {
    setMenuLanguages(restaurantId: $restaurantId, language2: $language2, language3: $language3, displayMode: $displayMode) {
      _id
      menuLanguage2
      menuLanguage3
      menuLanguageDisplay
      menuLanguageKitchenDisplay
      menuLanguageReceiptDisplay
      menuLanguageScreenDisplay
    }
  }
`

// Kitchen ticket, customer receipt and restaurant-app order screen, each set on
// its own (migration 118). The restaurant can also set these itself in the
// restaurant app's Printer Settings.
export const SET_TICKET_LANGUAGES = gql`
  mutation SetTicketLanguages($restaurantId: ID!, $kitchen: String, $receipt: String, $screen: String) {
    setTicketLanguages(restaurantId: $restaurantId, kitchen: $kitchen, receipt: $receipt, screen: $screen) {
      _id
      menuLanguageKitchenDisplay
      menuLanguageReceiptDisplay
      menuLanguageScreenDisplay
    }
  }
`

export const BULK_TOGGLE_ITEM_AVAILABILITY = gql`
  mutation BulkToggleItemAvailability($itemIds: [ID!]!, $isAvailable: Boolean!) {
    bulkToggleItemAvailability(itemIds: $itemIds, isAvailable: $isAvailable)
  }
`

export const NORMALIZE_RESTAURANT_CATEGORIES = gql`
  mutation NormalizeRestaurantCategories($restaurantId: ID!) {
    normalizeRestaurantCategories(restaurantId: $restaurantId) {
      mergedGroups
      itemsRetagged
      categoriesRemoved
    }
  }
`

// ==========================================
// MODIFIER MUTATIONS
// ==========================================

export const CREATE_ADVANCED_MODIFIER = gql`
  mutation CreateAdvancedModifier($restaurantId: ID!, $input: AdvancedModifierInput!) {
    createAdvancedModifier(restaurantId: $restaurantId, input: $input) {
      _id
      restaurantId
      name
      description
      modifierType
      isIngredient
      isModifier
      createdAt
      updatedAt
    }
  }
`

export const UPDATE_ADVANCED_MODIFIER = gql`
  mutation UpdateAdvancedModifier($id: ID!, $input: AdvancedModifierInput!) {
    updateAdvancedModifier(id: $id, input: $input) {
      _id
      name
      modifierType
      updatedAt
    }
  }
`

export const DELETE_ADVANCED_MODIFIER = gql`
  mutation DeleteAdvancedModifier($id: ID!) {
    deleteAdvancedModifier(id: $id)
  }
`

// ==========================================
// MODIFIER GROUP MUTATIONS
// ==========================================

export const CREATE_ADVANCED_MODIFIER_GROUP = gql`
  mutation CreateAdvancedModifierGroup($restaurantId: ID!, $input: AdvancedModifierGroupInput!) {
    createAdvancedModifierGroup(restaurantId: $restaurantId, input: $input) {
      _id
      restaurantId
      name
      description
      isTemplate
      selectionType
      minSelections
      maxSelections
      isRequired
      allowDuplicates
      displayOrder
      createdAt
      updatedAt
    }
  }
`

export const UPDATE_ADVANCED_MODIFIER_GROUP = gql`
  mutation UpdateAdvancedModifierGroup($id: ID!, $input: AdvancedModifierGroupInput!) {
    updateAdvancedModifierGroup(id: $id, input: $input) {
      _id
      name
      selectionType
      updatedAt
    }
  }
`

export const DELETE_ADVANCED_MODIFIER_GROUP = gql`
  mutation DeleteAdvancedModifierGroup($id: ID!) {
    deleteAdvancedModifierGroup(id: $id)
  }
`

export const DUPLICATE_ADVANCED_MODIFIER_GROUP = gql`
  mutation DuplicateAdvancedModifierGroup($id: ID!, $name: String) {
    duplicateAdvancedModifierGroup(id: $id, name: $name) {
      _id
      name
      description
      selectionType
      minSelections
      maxSelections
      exactSelections
      isRequired
      groupRequirement
      additionalCost
      allowDuplicates
      maxDuplicates
      duplicatePricingMode
      duplicatePrices
      displayOrder
      modifiers {
        _id
        modifier {
          _id
          name
          modifierType
        }
        priceOverrideMode
        fixedPrice
        variantPrices
        displayOrder
        isDefault
        isHidden
      }
    }
  }
`

// ==========================================
// MENU-ITEM LINKING MUTATIONS
// ==========================================

export const ADD_ITEM_TO_MENU = gql`
  mutation AddItemToMenu($menuId: ID!, $itemId: ID!) {
    addItemToMenu(menuId: $menuId, itemId: $itemId)
  }
`

export const REMOVE_ITEM_FROM_MENU = gql`
  mutation RemoveItemFromMenu($menuId: ID!, $itemId: ID!) {
    removeItemFromMenu(menuId: $menuId, itemId: $itemId)
  }
`

// ==========================================
// ATTACHMENT MUTATIONS
// ==========================================

export const ATTACH_MODIFIER_GROUP_TO_ITEM = gql`
  mutation AttachModifierGroupToItem($itemId: ID!, $modifierGroupId: ID!) {
    attachModifierGroupToItem(itemId: $itemId, modifierGroupId: $modifierGroupId)
  }
`

export const DETACH_MODIFIER_GROUP_FROM_ITEM = gql`
  mutation DetachModifierGroupFromItem($itemId: ID!, $modifierGroupId: ID!) {
    detachModifierGroupFromItem(itemId: $itemId, modifierGroupId: $modifierGroupId)
  }
`

export const ATTACH_MODIFIER_GROUP_TO_CATEGORY = gql`
  mutation AttachModifierGroupToCategory($restaurantId: ID!, $input: CategoryModifierAttachmentInput!) {
    attachModifierGroupToCategory(restaurantId: $restaurantId, input: $input) {
      success
      affectedCount
    }
  }
`

export const BULK_ATTACH_MODIFIER_GROUP_TO_ITEMS = gql`
  mutation BulkAttachModifierGroupToItems($itemIds: [ID!]!, $modifierGroupId: ID!) {
    bulkAttachModifierGroupToItems(itemIds: $itemIds, modifierGroupId: $modifierGroupId) {
      success
      affectedCount
    }
  }
`

export const DETACH_MODIFIER_GROUP_FROM_CATEGORY = gql`
  mutation DetachModifierGroupFromCategory($restaurantId: ID!, $categoryName: String!, $modifierGroupId: ID!) {
    detachModifierGroupFromCategory(restaurantId: $restaurantId, categoryName: $categoryName, modifierGroupId: $modifierGroupId)
  }
`

// ==========================================
// OPTION GROUP MUTATIONS
// ==========================================

export const CREATE_ADVANCED_OPTION_GROUP = gql`
  mutation CreateAdvancedOptionGroup($restaurantId: ID!, $input: AdvancedOptionGroupInput!) {
    createAdvancedOptionGroup(restaurantId: $restaurantId, input: $input) {
      _id
      name
      choices
      selectionType
      isRequired
      createdAt
    }
  }
`

export const UPDATE_ADVANCED_OPTION_GROUP = gql`
  mutation UpdateAdvancedOptionGroup($id: ID!, $input: AdvancedOptionGroupInput!) {
    updateAdvancedOptionGroup(id: $id, input: $input) {
      _id
      name
      choices
      selectionType
      isRequired
      updatedAt
    }
  }
`

export const DELETE_ADVANCED_OPTION_GROUP = gql`
  mutation DeleteAdvancedOptionGroup($id: ID!) {
    deleteAdvancedOptionGroup(id: $id)
  }
`

export const ATTACH_OPTION_GROUP_TO_ITEM = gql`
  mutation AttachOptionGroupToItem($itemId: ID!, $optionGroupId: ID!) {
    attachOptionGroupToItem(itemId: $itemId, optionGroupId: $optionGroupId)
  }
`

export const DETACH_OPTION_GROUP_FROM_ITEM = gql`
  mutation DetachOptionGroupFromItem($itemId: ID!, $optionGroupId: ID!) {
    detachOptionGroupFromItem(itemId: $itemId, optionGroupId: $optionGroupId)
  }
`

export const ATTACH_OPTION_GROUP_TO_CATEGORY = gql`
  mutation AttachOptionGroupToCategory($restaurantId: ID!, $categoryName: String!, $optionGroupIds: [ID!]!) {
    attachOptionGroupToCategory(restaurantId: $restaurantId, categoryName: $categoryName, optionGroupIds: $optionGroupIds) {
      success
      affectedCount
    }
  }
`

export const DETACH_OPTION_GROUP_FROM_CATEGORY = gql`
  mutation DetachOptionGroupFromCategory($restaurantId: ID!, $categoryName: String!, $optionGroupId: ID!) {
    detachOptionGroupFromCategory(restaurantId: $restaurantId, categoryName: $categoryName, optionGroupId: $optionGroupId)
  }
`

export const ATTACH_INGREDIENTS_TO_CATEGORY = gql`
  mutation AttachIngredientsToCategory($restaurantId: ID!, $categoryName: String!, $modifierIds: [ID!], $ingredientItemIds: [ID!]) {
    attachIngredientsToCategory(restaurantId: $restaurantId, categoryName: $categoryName, modifierIds: $modifierIds, ingredientItemIds: $ingredientItemIds) {
      success
      affectedCount
    }
  }
`

export const DETACH_INGREDIENT_FROM_CATEGORY = gql`
  mutation DetachIngredientFromCategory($restaurantId: ID!, $categoryName: String!, $modifierId: ID, $ingredientItemId: ID) {
    detachIngredientFromCategory(restaurantId: $restaurantId, categoryName: $categoryName, modifierId: $modifierId, ingredientItemId: $ingredientItemId)
  }
`

export const REORDER_CATEGORIES = gql`
  mutation ReorderCategories($restaurantId: ID!, $orderedCategoryIds: [ID!]!) {
    reorderCategories(restaurantId: $restaurantId, orderedCategoryIds: $orderedCategoryIds)
  }
`

export const REORDER_ITEMS_IN_CATEGORY = gql`
  mutation ReorderItemsInCategory($restaurantId: ID!, $categoryName: String!, $orderedItemIds: [ID!]!) {
    reorderItemsInCategory(restaurantId: $restaurantId, categoryName: $categoryName, orderedItemIds: $orderedItemIds)
  }
`

// ==========================================
// 86 MUTATIONS
// ==========================================

export const MARK_AS_EIGHTYSIX = gql`
  mutation MarkAsEightySix($restaurantId: ID!, $input: EightySixInput!) {
    markAsEightySix(restaurantId: $restaurantId, input: $input)
  }
`

export const UNMARK_EIGHTYSIX = gql`
  mutation UnmarkEightySix($targetType: String!, $targetId: ID!) {
    unmarkEightySix(targetType: $targetType, targetId: $targetId)
  }
`

// ==========================================
// AI UPLOAD MUTATIONS
// ==========================================

export const INITIATE_AI_MENU_UPLOAD = gql`
  mutation InitiateAIMenuUpload($restaurantId: ID!, $uploadMethod: UploadMethod!, $fileUrl: String!, $menuId: ID) {
    initiateAIMenuUpload(restaurantId: $restaurantId, uploadMethod: $uploadMethod, fileUrl: $fileUrl, menuId: $menuId) {
      _id
      restaurantId
      uploadMethod
      fileUrl
      aiProcessingStatus
      itemsImported
      itemsFailed
      createdAt
    }
  }
`

export const PROCESS_AI_MENU_UPLOAD = gql`
  mutation ProcessAIMenuUpload($uploadId: ID!, $extractedData: JSON!) {
    processAIMenuUpload(uploadId: $uploadId, extractedData: $extractedData) {
      _id
      aiProcessingStatus
      aiExtractedData
      completedAt
    }
  }
`


// ==========================================
// 86 MANAGEMENT MUTATIONS
// ==========================================

export const MARK_AS_EIGHTY_SIX = gql`
  mutation MarkAsEightySix($restaurantId: ID!, $input: EightySixInput!) {
    markAsEightySix(restaurantId: $restaurantId, input: $input)
  }
`

export const UNMARK_EIGHTY_SIX = gql`
  mutation UnmarkEightySix($targetType: String!, $targetId: ID!) {
    unmarkEightySix(targetType: $targetType, targetId: $targetId)
  }
`
